"use client";

import React from "react";
import { Instagram, Linkedin, Dribbble, ArrowRight, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="11643" className="flex flex-col gap-3">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-5 w-24 bg-muted/20 animate-pulse rounded-sm" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return null;
  }
  return <ul className="flex flex-col gap-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-foreground/70 hover:text-primary transition-colors text-sm font-medium font-sans">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function FooterMenu() {
  return <MenuProvider menu_id="3" className="w-full">
      <FooterMenuConsumer />
    </MenuProvider>;
}
function NewsletterFormContent() {
  const {
    handleSubmit,
    control,
    isSubmitting,
    isSubmitted,
    successMessage
  } = useWvcForm();
  if (isSubmitted) {
    return <div className="text-primary text-sm font-medium animate-in fade-in slide-in-from-bottom-2 font-sans">
        {successMessage || "Bedankt voor uw inschrijving!"}
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-end gap-2">
        <FormField control={control} name="email" rules={{
        required: "E-mailadres is verplicht",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ongeldig e-mailadres"
        }
      }} render={({
        field
      }) => <FormItem className="w-full space-y-0">
              <FormControl>
                <Input placeholder="Uw e-mailadres" {...field} className="border-0 border-b border-border rounded-none px-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-primary placeholder:text-foreground/40 h-10 transition-colors font-sans" />
              </FormControl>
              <FormMessage className="absolute text-xs mt-1" />
            </FormItem>} />
        <Button type="submit" variant="ghost" size="icon" disabled={isSubmitting} className="mb-[1px] hover:bg-transparent hover:text-primary text-foreground/60 transition-transform hover:translate-x-1">
          {isSubmitting ? <Loader className="size-4 animate-spin" /> : <ArrowRight className="size-5" />}
          <span className="sr-only">Inschrijven</span>
        </Button>
      </div>
    </Form>;
}
function NewsletterSection() {
  return <FormProvider formId="newsletter-footer" sectionName="footer" defaultValues={{
    email: ""
  }} successMessage="U bent ingeschreven!" className="w-full">
      <NewsletterFormContent />
    </FormProvider>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  const socials = [{
    name: "LinkedIn",
    icon: Linkedin,
    href: "https://linkedin.com"
  }, {
    name: "Instagram",
    icon: Instagram,
    href: "https://instagram.com"
  }, {
    name: "Contact",
    icon: Dribbble,
    href: "/ContactPage"
  }];
  return <footer id="voetnoten-en-contactgegevens" className="bg-background text-foreground pt-20 pb-8 border-t border-border">
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-20">
          
          {}
          <div className="flex flex-col gap-6">
            <Link to="/HomePage" className="block w-fit">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579270/img/image_t7urh952.png" alt="CuraNova Zorg Logo" className="h-7 w-auto object-contain" />
            </Link>
            <p className="text-foreground/70 text-sm leading-relaxed max-w-xs font-sans">
              CuraNova Zorg biedt persoonsgerichte zorg en ondersteuning aan huis. Wij werken met gediplomeerde professionals aan uw welzijn en herstel.
            </p>
          </div>

          {}
          <div className="flex flex-col gap-6">
            <h3 className="text-base font-semibold tracking-tight text-primary font-serif">Navigatie</h3>
            <FooterMenu />
          </div>

          {}
          <div className="flex flex-col gap-6">
            <h3 className="text-base font-semibold tracking-tight text-primary font-serif">Contact & Volg ons</h3>
            <ul className="flex flex-col gap-3">
              {socials.map((social, i) => <li key={social.name} data-index={i}>
                  <Link to={social.href === "/ContactPage" ? "/ContactPage" : social.href} newTab={social.href.startsWith("http")} className="group flex items-center gap-3 text-foreground/70 hover:text-primary transition-colors text-sm font-medium font-sans">
                    <social.icon className="size-4 transition-transform group-hover:scale-110" />
                    <span>{social.name}</span>
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div className="flex flex-col gap-6">
            <h3 className="text-base font-semibold tracking-tight text-primary font-serif">Nieuwsbrief</h3>
            <p className="text-foreground/70 text-sm font-sans">
              Blijf op de hoogte van onze diensten in regio Den Haag, Rotterdam en omliggende gemeenten.
            </p>
            <NewsletterSection />
          </div>

        </div>

        <Separator className="bg-border mb-8" />

        {}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-foreground/50 font-sans">
          <p>&copy; {currentYear} CuraNova Zorg. Alle rechten voorbehouden.</p>
          <p className="font-serif italic tracking-wider opacity-80">Zorg met aandacht en continuïteit</p>
        </div>
      </div>
    </footer>;
}